/**
 * 監査ログ横断ビュー画面 (OPS-AUD-1/2, issue #198 Phase 5)。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退。
 * 製品セレクタ・製品バッジは削除済）。
 *
 * spec/ai-inbox-core/admin-ops/wireframes.md §7 OPS_AUDIT_LOG
 *
 * - PC 専用（max-w-7xl 想定、admin-ops は内部運用のみ）
 * - event_kind + actor_type + 期間 + workspace でフィルタ
 * - 行クリック → 詳細展開（before/after JSON, PII マスキング済）
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { type AuditLogRow, type AuditLogsQuery, fetchAuditLogs } from '../../lib/audit-logs-api';

interface AuditLogsPageProps {
  apiBaseUrl: string;
}

const EVENT_KINDS = [
  'リードのステータスを更新する',
  'ワークスペースを解約する',
  'Stripe Webhook を受信・反映する',
  'AIトリアージを実行する',
  'AI ON-OFF 切替',
  'Lead を削除',
];

export function AuditLogsPage({ apiBaseUrl }: AuditLogsPageProps) {
  const [eventKind, setEventKind] = useState<string>('');
  const [actorType, setActorType] = useState<string>('');
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const limit = 50;

  const query: AuditLogsQuery = useMemo(
    () => ({
      eventKind: eventKind || undefined,
      actorType: actorType || undefined,
      workspaceId: workspaceId || undefined,
      from: from ? `${from}T00:00:00Z` : undefined,
      to: to ? `${to}T23:59:59Z` : undefined,
      limit,
      offset: page * limit,
    }),
    [eventKind, actorType, workspaceId, from, to, page],
  );

  const result = useQuery({
    queryKey: ['admin-ops-audit-logs', query],
    queryFn: () => fetchAuditLogs(apiBaseUrl, query),
  });

  function resetPage<T>(setter: (v: T) => void): (v: T) => void {
    return (v) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-wider text-slate-500">ALYS Internal</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">監査ログ横断ビュー</h1>
        <p className="mt-2 text-sm text-slate-600">
          営業受付カウンターの <code className="font-mono">audit_logs</code> を検索。PII
          はサーバ側で マスキング済。
        </p>
      </header>

      <section
        aria-label="フィルタ"
        className="mb-4 rounded-md border border-slate-200 bg-white p-4"
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <label className="text-xs text-slate-600">
            event_kind / action_name
            <input
              list="event-kind-options"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={eventKind}
              onChange={(e) => resetPage(setEventKind)(e.target.value)}
              placeholder="全て"
              data-testid="filter-event-kind"
            />
            <datalist id="event-kind-options">
              {EVENT_KINDS.map((k) => (
                <option key={k} value={k} />
              ))}
            </datalist>
          </label>

          <label className="text-xs text-slate-600">
            actor_type
            <select
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={actorType}
              onChange={(e) => resetPage(setActorType)(e.target.value)}
              data-testid="filter-actor-type"
            >
              <option value="">全て</option>
              <option value="alys_owner">alys_owner</option>
              <option value="workspace_user">workspace_user</option>
              <option value="system">system</option>
            </select>
          </label>

          <label className="text-xs text-slate-600">
            workspace_id
            <input
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono"
              value={workspaceId}
              onChange={(e) => resetPage(setWorkspaceId)(e.target.value)}
              placeholder="uuid"
              data-testid="filter-workspace"
            />
          </label>

          <label className="text-xs text-slate-600">
            開始日
            <input
              type="date"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={from}
              onChange={(e) => resetPage(setFrom)(e.target.value)}
              data-testid="filter-from"
            />
          </label>

          <label className="text-xs text-slate-600">
            終了日
            <input
              type="date"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={to}
              onChange={(e) => resetPage(setTo)(e.target.value)}
              data-testid="filter-to"
            />
          </label>
        </div>
      </section>

      <section className="rounded-md border border-slate-200 bg-white">
        {result.isLoading && <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>}
        {result.isError && (
          <p className="px-4 py-6 text-sm text-rose-600">
            取得に失敗しました: {(result.error as Error).message}
          </p>
        )}
        {result.data && (
          <>
            <AuditLogsTable rows={result.data.rows} />
            <Pagination
              page={page}
              limit={result.data.limit}
              hasNext={result.data.rows.length >= result.data.limit}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />
            <FetchedInfo fetched={result.data.fetched} configured={result.data.configured} />
          </>
        )}
      </section>
    </main>
  );
}

function AuditLogsTable({ rows }: { rows: AuditLogRow[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-500" data-testid="empty-state">
        該当する監査ログはありません。
      </p>
    );
  }
  return (
    <table className="w-full text-left text-sm" data-testid="audit-logs-table">
      <thead className="border-b border-slate-200 text-xs text-slate-500">
        <tr>
          <th className="px-3 py-2 font-medium">時刻</th>
          <th className="px-3 py-2 font-medium">ws</th>
          <th className="px-3 py-2 font-medium">actor</th>
          <th className="px-3 py-2 font-medium">Action</th>
          <th className="px-3 py-2 font-medium">Target</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isOpen = openId === row.id;
          return (
            <tr key={row.id} className="border-b border-slate-100">
              <td colSpan={5} className="p-0">
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : row.id)}
                  className="grid w-full grid-cols-5 items-center gap-0 px-0 py-0 text-left hover:bg-slate-50"
                  data-testid={`row-${row.id}`}
                >
                  <span className="px-3 py-2 font-mono text-xs text-slate-600">
                    {formatTime(row.occurredAt)}
                  </span>
                  <span className="px-3 py-2 font-mono text-xs text-slate-500">
                    {row.workspaceId?.slice(0, 8) ?? '—'}
                  </span>
                  <span className="px-3 py-2 text-xs">{row.actorType}</span>
                  <span className="px-3 py-2 text-xs">{row.actionName}</span>
                  <span className="px-3 py-2 text-xs text-slate-600">{row.targetType}</span>
                </button>
                {isOpen && <RowDetail row={row} />}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RowDetail({ row }: { row: AuditLogRow }) {
  return (
    <div
      className="border-t border-slate-100 bg-slate-50 px-3 py-3"
      data-testid={`row-detail-${row.id}`}
    >
      <dl className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
        <div>
          <dt className="text-slate-500">target_id</dt>
          <dd className="font-mono">{row.targetId ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">reason</dt>
          <dd className="whitespace-pre-wrap">{row.reason ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">before_value</dt>
          <dd>
            <pre className="overflow-x-auto rounded bg-white p-2 font-mono text-[11px]">
              {JSON.stringify(row.beforeValue, null, 2)}
            </pre>
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">after_value</dt>
          <dd>
            <pre className="overflow-x-auto rounded bg-white p-2 font-mono text-[11px]">
              {JSON.stringify(row.afterValue, null, 2)}
            </pre>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function Pagination({
  page,
  limit,
  hasNext,
  onPrev,
  onNext,
}: {
  page: number;
  limit: number;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-slate-600">
      <span>
        ページ {page + 1}（{limit} 件 / ページ）
      </span>
      <span className="flex gap-2">
        <button
          type="button"
          disabled={page === 0}
          onClick={onPrev}
          className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          data-testid="page-prev"
        >
          前へ
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={onNext}
          className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          data-testid="page-next"
        >
          次へ
        </button>
      </span>
    </div>
  );
}

function FetchedInfo({ fetched, configured }: { fetched: number; configured: boolean }) {
  return (
    <div
      className="border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500"
      data-testid="fetched-info"
    >
      <span className="font-medium">取得件数: </span>
      {fetched}
      {!configured && <span className="ml-1 rounded bg-slate-100 px-1 text-slate-500">未設定</span>}
    </div>
  );
}

function formatTime(iso: string): string {
  // 入力例: 2026-05-20T09:18:00Z → "2026-05-20 09:18"
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}
